import { cn } from "@/lib/cn";

/**
 * "Tills — 2 of 3", with a bar.
 *
 * The number is the point and the bar is the decoration, so the number is first
 * and is readable on its own: a shopkeeper glancing at this on a phone in
 * sunlight should not have to judge the length of a stripe. The bar is
 * `aria-hidden` for the same reason — a screen reader announcing a progress
 * value after the text has just said "2 of 3" is saying it twice.
 *
 * Three states, and the third is why this is a component rather than a div:
 *
 * *   **Unlimited** shows no bar at all. A full bar would read as "you are out",
 *     and an empty one as "you have none".
 * *   **Nearly full** turns amber at 80%, which is a warning and not yet a
 *     problem.
 * *   **Full** turns red and says so in words. This is the state where somebody
 *     is about to try something that will be refused, and the point of showing
 *     meters at all is that they find out here rather than at the till.
 */
export interface UsageMeterProps {
  label: string;
  used: number;
  /** Null means unlimited. */
  limit: number | null;
  /** 0–100, or null when unlimited. */
  percent: number | null;
  isExhausted: boolean;
}

const WARN_AT = 80;

export function UsageMeter({ label, used, limit, percent, isExhausted }: UsageMeterProps) {
  const unlimited = limit === null;
  const nearlyFull = !unlimited && !isExhausted && (percent ?? 0) >= WARN_AT;

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-ink-600">{label}</span>
        <span
          className={cn(
            "text-sm font-medium tabular-nums",
            isExhausted ? "text-danger-700" : "text-ink-900",
          )}
        >
          {unlimited ? `${used} · no limit` : `${used} of ${limit}`}
        </span>
      </div>

      {unlimited ? null : (
        <div className="h-1.5 w-full overflow-hidden rounded-pill bg-canvas" aria-hidden>
          <div
            className={cn(
              "h-full rounded-pill transition-[width]",
              isExhausted ? "bg-danger-500" : nearlyFull ? "bg-warn-500" : "bg-brand-500",
            )}
            style={{ width: `${percent ?? 0}%` }}
          />
        </div>
      )}

      {isExhausted && (
        <p className="text-xs text-danger-700">
          You have used all of these. Ask SmartSpaza for a bigger package to add more.
        </p>
      )}
    </div>
  );
}
